import type { Customer } from '../hooks/useCustomers';
import { downloadBytes } from '../../../utils/download';
import { todayString } from '../../../utils/dateUtils';
import { buildXlsx, XLSX_MIME, type XlsxColumn, type XlsxTable } from '../../../utils/xlsx';

/**
 * 客户导出的列定义：顺序、表头、取数三件事只在这里写一次，
 * 「导出设置」面板与 .xlsx 产物都读这一份，避免面板能勾的东西和文件里的列各长一套。
 *
 * 列序按「先确认是谁 → 再联系 → 再归类 → 再落地地址」的阅读顺序排：
 * 拿到台账的人第一眼看姓名和类型，第二眼就要能打电话，最后才是地址这类长文本。
 */
export interface CustomerExportColumn {
  key: string;
  header: string;
  /** 固定列恒导出；可选列由「导出设置」勾选 */
  optional: boolean;
  value: (customer: Customer) => string;
  /** 列宽（Excel 字符单位）。长文本列要给，否则 Excel 按默认窄列显示，地址一屏看不全 */
  width?: number;
}

export const CUSTOMER_EXPORT_COLUMNS: readonly CustomerExportColumn[] = [
  { key: 'customerType', header: '客户类型', optional: false, value: (c) => c.customerTypeName ?? '' },
  { key: 'fullName', header: '客户姓名', optional: false, value: (c) => c.fullName ?? '' },
  // 联系方式是自由文本：0 开头的座机只要被 Excel 当成数字就永久丢掉前导 0，文本格里它才原样在
  { key: 'phone', header: '联系方式', optional: false, value: (c) => c.phone ?? '', width: 16 },
  { key: 'backupPhone', header: '备用联系方式', optional: true, value: (c) => c.backupPhone ?? '', width: 16 },
  { key: 'location', header: '所在地', optional: true, value: (c) => c.location ?? '' },
  { key: 'tags', header: '客户标签', optional: true, value: (c) => (c.tags ?? []).map((t) => t.name).join('、'), width: 20 },
  { key: 'emergencyContact', header: '紧急联系人', optional: true, value: (c) => c.emergencyContact ?? '' },
  { key: 'emergencyPhone', header: '紧急联系人电话', optional: true, value: (c) => c.emergencyPhone ?? '', width: 16 },
  { key: 'companyName', header: '公司/单位', optional: true, value: (c) => c.companyName ?? '', width: 20 },
  { key: 'email', header: '邮箱', optional: true, value: (c) => c.email ?? '', width: 22 },
  { key: 'address', header: '详细地址', optional: true, value: (c) => c.address ?? '', width: 30 },
  { key: 'introduction', header: '备注', optional: true, value: (c) => c.introduction ?? '', width: 30 },
];

/** 「导出设置」里能勾的那几项，顺序与导出列序一致 */
export const OPTIONAL_EXPORT_KEYS = CUSTOMER_EXPORT_COLUMNS.filter((c) => c.optional).map((c) => c.key);

/** 勾选结果存本机而不是存库：这是每个人的看表习惯，不是客户的属性 */
const STORAGE_KEY = 'customer-export-optional-fields';

/**
 * 读勾选的可选列。存过就按存的来（**包括「一个都没勾」**——只留三列固定列是合法选择，
 * 不能拿「空数组」当「没设置过」，否则用户清空勾选后下次又被塞满）。
 */
export function readSelectedOptionalKeys(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [...OPTIONAL_EXPORT_KEYS];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...OPTIONAL_EXPORT_KEYS];
    const known = new Set<string>(OPTIONAL_EXPORT_KEYS);
    return OPTIONAL_EXPORT_KEYS.filter((key) => parsed.includes(key) && known.has(key));
  } catch {
    return [...OPTIONAL_EXPORT_KEYS];
  }
}

export function saveSelectedOptionalKeys(keys: readonly string[]): void {
  try {
    const known = new Set<string>(OPTIONAL_EXPORT_KEYS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keys.filter((key) => known.has(key))));
  } catch {
    // 隐私模式 / 配额满：勾选项丢一次不影响导出本身，不弹错误打断用户
  }
}

/** 本次导出真正要写的列：固定列 + 勾选中的可选列，仍按 CUSTOMER_EXPORT_COLUMNS 的原序 */
export function resolveExportColumns(selectedOptionalKeys: readonly string[]): CustomerExportColumn[] {
  const picked = new Set<string>(selectedOptionalKeys);
  return CUSTOMER_EXPORT_COLUMNS.filter((column) => !column.optional || picked.has(column.key));
}

/**
 * 纯函数：列 + 行 → 待产出的表。浏览器实测打这个口子看列与值，不碰下载。
 * 全部列都是文本格（value 返回的就是字符串），所以 Excel 不会去猜类型。
 */
export function buildCustomerTable(customers: readonly Customer[], columns: readonly CustomerExportColumn[]): XlsxTable {
  return {
    sheetName: '客户列表',
    columns: columns.map<XlsxColumn>((column) => ({ header: column.header, width: column.width })),
    rows: customers.map((customer) => columns.map((column) => column.value(customer))),
  };
}

/** 将客户列表导出为 .xlsx 文件并触发下载 */
export function exportCustomersXlsx(
  customers: Customer[],
  filename?: string,
  selectedOptionalKeys: readonly string[] = readSelectedOptionalKeys(),
): void {
  const table = buildCustomerTable(customers, resolveExportColumns(selectedOptionalKeys));
  downloadBytes(
    filename ?? `客户列表_${todayString()}.xlsx`,
    buildXlsx(table),
    XLSX_MIME,
  );
}
